import { lazy } from "react";
import { PortfolioGuard, TranslationText } from "components";
import { ProtectedRoute } from "components/ProtectedRoute/ProtectedRoute";
import { NavTabRoutes } from "layouts/NavTabLayout/NavTab/NavTabRoutes";
import { NavTabPath } from "layouts/NavTabLayout/NavTab/types";
import { PortfolioNavigationHeaderLayout } from "layouts/PortfolioNavigationHeaderLayout/PortfolioNavigationHeaderLayout";
import { Navigate, Outlet, useRoutes } from "react-router-dom";

const Overview = lazy(() =>
  import("./overview").then((module) => ({ default: module.OverviewPage }))
);
const Holdings = lazy(() =>
  import("./holdings").then((module) => ({ default: module.HoldingsView }))
);
const Holding = lazy(() =>
  import("./holdings/[holdingId]").then((module) => ({
    default: module.HoldingPage,
  }))
);
const Transactions = lazy(() =>
  import("./transactions").then((module) => ({
    default: module.TransactionsPage,
  }))
);
const TransactionDetails = lazy(() =>
  import("./transactions/[transactionId]").then((module) => ({
    default: module.TransactionDetailsPage,
  }))
);
const News = lazy(() =>
  import("../news").then((module) => ({ default: module.NewsPage }))
);

const NewsArticle = lazy(() =>
  import("../news/[articleKey]").then((module) => ({
    default: module.NewsArticlePage,
  }))
);

const Orders = lazy(() =>
  import("./orders").then((module) => ({ default: module.OrdersPage }))
);
const OrderDetails = lazy(() =>
  import("./orders/[orderId]").then((module) => ({
    default: module.OrderDetailsPage,
  }))
);
const Documents = lazy(() =>
  import("./documents").then((module) => ({ default: module.DocumentsPage }))
);
const Contact = lazy(() =>
  import("./contact").then((module) => ({ default: module.ContactPage }))
);

const Performance = lazy(() =>
  import("./performance").then((module) => ({
    default: module.PerformancePage,
  }))
);

const Bookkeeping = lazy(() =>
  import("./bookkeeping").then((module) => ({
    default: module.BookkeepingsPage,
  }))
);
const Limits = lazy(() =>
  import("./limits").then((module) => ({ default: module.LimitsPage }))
);
const Reports = lazy(() =>
  import("./reports").then((module) => ({ default: module.ReportsPage }))
);

const Securities = lazy(() =>
  import("./securities").then((module) => ({ default: module.SecuritiesPage }))
);

const SecurityDetails = lazy(() =>
  import("./securities/[securityId]").then((module) => ({
    default: module.SecurityDetailsPage,
  }))
);
const Issuers = lazy(() =>
  import("./issuers").then((module) => ({
    default: module.IssuersPage,
  }))
);
const IssuerDetails = lazy(() =>
  import("./issuers/[issuerId]").then((module) => ({
    default: module.IssuerDetailsPage,
  }))
);
const Sustainability = lazy(() =>
  import("../sustainability").then((module) => ({
    default: module.SustainabilityPage,
  }))
);

const Webbinar = lazy(() =>
  import("../webbinar").then((module) => ({ default: module.WebbinarPage }))
);

const WebbinarDetail = lazy(() =>
  import("../webbinar/[webbinarId]").then((module) => ({
    default: module.WebbinarDetailPage,
  }))
);

const Exposure = lazy(() =>
  import("./exposure").then((module) => ({ default: module.ExposurePage }))
);

export const portfolioTabRoutes = (allowedRoutes: string[]): NavTabPath[] => {
  return [
    {
      path: "overview",
      icon: <span className="text-2xl mgc_home_3_line"></span>,
      tabLabel: <TranslationText translationKey="navTab.tabs.overview" />,
      element: (
        <ProtectedRoute isAllowed={allowedRoutes.includes("overview")}>
          <PortfolioGuard>
            <Overview />
          </PortfolioGuard>
        </ProtectedRoute>
      ),
    },
    {
      path: "holdings",
      icon: <span className="text-2xl mgc_chart_pie_2_line"></span>,
      tabLabel: <TranslationText translationKey="navTab.tabs.holdings" />,
      element: (
        <ProtectedRoute isAllowed={allowedRoutes.includes("holdings")}>
          <Outlet />
        </ProtectedRoute>
      ),
      children: [
        {
          index: true,
          element: (
            <PortfolioGuard>
              <Holdings />
            </PortfolioGuard>
          ),
        },
        {
          path: ":holdingId",
          element: <Holding />,
        },
      ],
    },
    {
      path: "transactions",
      icon: <span className="text-2xl mgc_exchange_euro_line"></span>,
      tabLabel: <TranslationText translationKey="navTab.tabs.transactions" />,
      element: (
        <ProtectedRoute isAllowed={allowedRoutes.includes("transactions")}>
          <Outlet />
        </ProtectedRoute>
      ),
      children: [
        {
          index: true,
          element: <Transactions />,
        },
        {
          path: ":transactionId",
          element: <TransactionDetails />,
        },
      ],
    },
    {
      path: "orders",
      icon: <span className="text-2xl mgc_transfer_horizontal_line"></span>,
      tabLabel: <TranslationText translationKey="navTab.tabs.orders" />,
      element: (
        <ProtectedRoute isAllowed={allowedRoutes.includes("orders")}>
          <Outlet />
        </ProtectedRoute>
      ),
      children: [
        {
          index: true,
          element: <Orders />,
        },
        {
          path: ":orderId",
          element: <OrderDetails />,
        },
      ],
    },
    {
      path: "performance",
      icon: <span className="text-2xl mgc_trending_up_line"></span>,
      tabLabel: <TranslationText translationKey="navTab.tabs.performance" />,
      element: (
        <ProtectedRoute isAllowed={allowedRoutes.includes("performance")}>
          <PortfolioGuard>
            <Performance />
          </PortfolioGuard>
        </ProtectedRoute>
      ),
    },
    {
      path: "exposure",
      icon: <span className="text-2xl mgc_scan_line"></span>,
      tabLabel: "Genomlysning",
      element: (
        <ProtectedRoute isAllowed={allowedRoutes.includes("exposure")}>
          <PortfolioGuard>
            <Exposure />
          </PortfolioGuard>
        </ProtectedRoute>
      ),
    },
    {
      path: "limits",
      icon: <span className="text-2xl mgc_balance_line"></span>,
      tabLabel: <TranslationText translationKey="navTab.tabs.limits" />,
      element: (
        <ProtectedRoute isAllowed={allowedRoutes.includes("limits")}>
          <PortfolioGuard>
            <Limits />
          </PortfolioGuard>
        </ProtectedRoute>
      ),
    },
    {
      path: "reports",
      icon: <span className="text-2xl mgc_document_2_line"></span>,
      tabLabel: <TranslationText translationKey="navTab.tabs.reports" />,
      element: (
        <ProtectedRoute isAllowed={allowedRoutes.includes("reports")}>
          <PortfolioGuard>
            <Reports />
          </PortfolioGuard>
        </ProtectedRoute>
      ),
    },
    {
      path: "bookkeeping",
      icon: <span className="text-2xl mgc_book_2_line"></span>,
      tabLabel: <TranslationText translationKey="navTab.tabs.bookkeeping" />,
      element: (
        <ProtectedRoute isAllowed={allowedRoutes.includes("bookkeeping")}>
          <PortfolioGuard>
            <Bookkeeping />
          </PortfolioGuard>
        </ProtectedRoute>
      ),
    },
    {
      path: "securities",
      icon: <span className="text-2xl mgc_list_search_line"></span>,
      tabLabel: <TranslationText translationKey="navTab.tabs.securities" />,

      element: (
        <ProtectedRoute isAllowed={allowedRoutes.includes("securities")}>
          <Outlet />
        </ProtectedRoute>
      ),
      children: [
        {
          index: true,
          element: (
            <PortfolioGuard>
              <Securities />
            </PortfolioGuard>
          ),
        },
        {
          path: ":securityId",
          element: <SecurityDetails />,
        },
        {
          path: ":securityId/:issuerId",
          element: <IssuerDetails />,
        },
      ],
    },
    {
      path: "issuers",
      icon: <span className="text-2xl mgc_building_1_line"></span>,
      tabLabel: <TranslationText translationKey="navTab.tabs.issuers" />,
      element: (
        <ProtectedRoute isAllowed={allowedRoutes.includes("issuers")}>
          <Outlet />
        </ProtectedRoute>
      ),
      children: [
        {
          index: true,
          element: (
            <PortfolioGuard>
              <Issuers />
            </PortfolioGuard>
          ),
        },
        {
          path: ":issuerId",
          element: <IssuerDetails />,
        },
      ],
    },
    {
      path: "news",
      icon: <span className="text-2xl mgc_announcement_line"></span>,
      tabLabel: <TranslationText translationKey="navTab.tabs.news" />,
      // Editorial content, so it is in no `allowedRoutes` list. Without this the
      // tab would fall back to that list, render disabled, and — because Headless
      // UI refuses to select a disabled tab — hand the highlight to a later tab.
      disabled: false,
      element: (
        // Issuer news is editorial, not account data: every user sees the tab,
        // the way webbinars do, rather than waiting on a contact tag.
        <ProtectedRoute isAllowed={true}>
          <Outlet />
        </ProtectedRoute>
      ),
      children: [
        {
          index: true,
          element: <News />,
        },
        {
          path: ":articleKey",
          element: <NewsArticle />,
        },
      ],
    },
    {
      path: "sustainability",
      icon: <span className="text-2xl mgc_leaf_line"></span>,
      tabLabel: <TranslationText translationKey="navTab.tabs.sustainability" />,
      element: (
        <ProtectedRoute isAllowed={allowedRoutes.includes("sustainability")}>
          <PortfolioGuard>
            <Sustainability />
          </PortfolioGuard>
        </ProtectedRoute>
      ),
    },
    {
      path: "documents",
      icon: <span className="text-2xl mgc_pdf_line"></span>,
      tabLabel: <TranslationText translationKey="navTab.tabs.documents" />,
      element: (
        <ProtectedRoute isAllowed={allowedRoutes.includes("documents")}>
          <PortfolioGuard>
            <Documents />
          </PortfolioGuard>
        </ProtectedRoute>
      ),
    },
    {
      path: "webbinar",
      icon: <span className="text-2xl mgc_video_line"></span>,
      tabLabel: <TranslationText translationKey="navTab.tabs.webbinar" />,
      element: (
        <ProtectedRoute isAllowed={allowedRoutes.includes("webbinar")}>
          <Outlet />
        </ProtectedRoute>
      ),
      children: [
        {
          index: true,
          element: <Webbinar />,
        },
        {
          path: ":webbinarId",
          element: <WebbinarDetail />,
        },
      ],
    },
  ];
};

const contact = {
  path: "contact",
  icon: <span className="text-2xl mgc_phone_line"></span>,
  tabLabel: <TranslationText translationKey="navTab.tabs.contact" />,
  element: <Contact />,
};

export const portfolioRoutes = (routes: NavTabPath[]) => {
  return [
    {
      path: "",
      element: (
        <Navigate
          to={routes.find((route) => !route.disabled)?.path ?? "/contact"}
          replace
        />
      ),
    },
    {
      path: "",
      element: <PortfolioNavigationHeaderLayout />,
      children: [
        {
          path: "*",
          element: <NavTabRoutes routes={routes} />,
        },
      ],
    },
  ];
};

interface PortfolioRoutesProps {
  allowedRoutes: string[];
}

export const PortfolioRoutes = ({ allowedRoutes }: PortfolioRoutesProps) => {
  const accessedRoutes = portfolioTabRoutes(allowedRoutes).map((route) => ({
    ...route,
    disabled: route.disabled ?? !allowedRoutes.includes(route.path),
  }));

  return useRoutes(portfolioRoutes([...accessedRoutes, contact]));
};
